import React, { useState } from "react";
import BackButtom from "../../../components/backButton";
import DataGrid, { ItemField } from "../../../components/datagrid";
import Icons from "../../../components/icons-helper";
import ButtonRound from "../../../components/button-action";
import {
  CityRuleCountModel,
  TaxationLayout,
  TaxationRuleCreateModel,
  TaxationRuleModel,
  TaxationRuleUpdateModel,
} from "../../../../domain/models/service-taxation-code";
import { NationalServiceModel } from "../../../../domain/models/national-service";
import { ComplementaryLawModel } from "../../../../data/usecases/complementary-law/remote-complementary-law";
import { theme } from "../../../styles/theme";
import RuleModal from "./components/rule-modal";
import * as S from "./styles";

type PageProps = {
  // city search
  cities: CityRuleCountModel[];
  citySearch: string;
  cityLoading: boolean;
  cityTotalPages: number;
  onCitySearchChange: (search: string) => void;
  onCityPageChange: (page: number) => void;
  onSelectCity: (city: CityRuleCountModel) => void;
  // city rules
  selectedCity: CityRuleCountModel | null;
  rules: TaxationRuleModel[];
  rulesLoading: boolean;
  activeLayout: TaxationLayout;
  onLayoutChange: (layout: TaxationLayout) => void;
  onBackToCities: () => void;
  // CRUD
  onOpenCreate: () => void;
  onOpenEdit: (rule: TaxationRuleModel) => void;
  onDelete: (id: number) => void;
  // modal
  modalOpen: boolean;
  editingRule: TaxationRuleModel | null;
  onCloseModal: () => void;
  onSave: (data: TaxationRuleCreateModel | TaxationRuleUpdateModel) => void;
  // picker
  nationalServices: NationalServiceModel[];
  complementaryLaws: ComplementaryLawModel[];
  pickerLoading: boolean;
  onSearchService: (query: string, layout: TaxationLayout) => void;
};

const TaxationCodeAdminPage: React.FC<PageProps> = ({
  cities,
  citySearch,
  cityLoading,
  cityTotalPages,
  onCitySearchChange,
  onCityPageChange,
  onSelectCity,
  selectedCity,
  rules,
  rulesLoading,
  activeLayout,
  onLayoutChange,
  onBackToCities,
  onOpenCreate,
  onOpenEdit,
  onDelete,
  modalOpen,
  editingRule,
  onCloseModal,
  onSave,
  nationalServices,
  complementaryLaws,
  pickerLoading,
  onSearchService,
}) => {
  const [confirmDelete, setConfirmDelete] = useState<TaxationRuleModel | null>(null);

  // --- City Search View ---
  if (!selectedCity) {
    const cityFields: ItemField[] = [
      {
        title: "IBGE",
        key: "ibge",
        renderItem: (data: CityRuleCountModel) => data.ibge,
      },
      {
        title: "Cidade",
        key: "name",
        renderItem: (data: CityRuleCountModel) => data.name,
      },
      {
        title: "UF",
        key: "uf",
        renderItem: (data: CityRuleCountModel) => data.uf || "-",
      },
      {
        title: "Regras",
        key: "rulesCount",
        renderItem: (data: CityRuleCountModel) => String(data.rulesCount),
      },
      {
        title: "Ações",
        key: "actions",
        renderItem: (data: CityRuleCountModel) => (
          <ButtonRound
            backgroundColor={theme.colors.darkBlue}
            onClick={() => onSelectCity(data)}
            title="Abrir"
            color={theme.colors.lightBlue}
          >
            <Icons height={15} width={15} name="eye" color={theme.colors.primary} />
          </ButtonRound>
        ),
      },
    ];

    return (
      <S.Wrapper>
        <BackButtom text="Códigos de Tributação — Cidades" />
        <S.InfoBox>
          Configura os códigos de serviço e tributação por município/empresa. Permite definir regras específicas para cada cidade, garantindo que o código correto seja enviado na emissão de NFS-e Municipal e Nacional.
        </S.InfoBox>
        <S.CardWrapper>
          <S.SearchInput
            type="text"
            placeholder="Buscar por IBGE (7 díg), CEP (8 díg) ou nome da cidade..."
            value={citySearch}
            onChange={(e) => onCitySearchChange(e.target.value)}
          />
        </S.CardWrapper>
        <DataGrid
          loading={cityLoading}
          fields={cityFields}
          data={cities}
          totalPages={cityTotalPages}
          changePage={onCityPageChange}
        />
      </S.Wrapper>
    );
  }

  // --- City Rules View ---
  const ruleFields: ItemField[] = [
    {
      title: "Serviço",
      key: "serviceLabel",
      renderItem: (data: TaxationRuleModel) =>
        data.serviceCode
          ? `${data.serviceCode} — ${data.serviceLabel}`
          : data.serviceLabel || "Todos os serviços",
    },
    ...(activeLayout === "MUNICIPAL"
      ? [
          {
            title: "Código",
            key: "codigo",
            renderItem: (data: TaxationRuleModel) => data.codigo || "-",
          },
        ]
      : []),
    {
      title: "Cód. Tributação",
      key: "codigoTributacao",
      renderItem: (data: TaxationRuleModel) => data.codigoTributacao || "-",
    },
    {
      title: "Enviar",
      key: "sendCode",
      renderItem: (data: TaxationRuleModel) => (
        <S.Badge $active={data.sendCode}>{data.sendCode ? "Sim" : "Não"}</S.Badge>
      ),
    },
    {
      title: "Ações",
      key: "actions",
      renderItem: (data: TaxationRuleModel) => (
        <S.ActionsRow>
          <ButtonRound
            backgroundColor={theme.colors.darkBlue}
            onClick={() => onOpenEdit(data)}
            title="Editar"
            color={theme.colors.lightBlue}
          >
            <Icons height={15} width={15} name="edit" color={theme.colors.primary} />
          </ButtonRound>
          <ButtonRound
            backgroundColor={theme.colors.darkBlue}
            onClick={() => setConfirmDelete(data)}
            title="Excluir"
            color={theme.colors.red}
          >
            <Icons height={15} width={15} name="trash" color={theme.colors.red} />
          </ButtonRound>
        </S.ActionsRow>
      ),
    },
  ];

  return (
    <S.Wrapper>
      <S.BackHeader>
        <S.BackButton onClick={onBackToCities}>← Voltar</S.BackButton>
        <S.PageTitle>Regras — {selectedCity.name} ({selectedCity.uf})</S.PageTitle>
      </S.BackHeader>

      <S.CardWrapper>
        <S.LayoutTabs>
          <S.LayoutTab $active={activeLayout === "NACIONAL"} onClick={() => onLayoutChange("NACIONAL")}>
            Nacional
          </S.LayoutTab>
          <S.LayoutTab $active={activeLayout === "MUNICIPAL"} onClick={() => onLayoutChange("MUNICIPAL")}>
            Municipal
          </S.LayoutTab>
        </S.LayoutTabs>
        <S.CreateButton onClick={onOpenCreate}>+ Nova Regra</S.CreateButton>
      </S.CardWrapper>

      <DataGrid
        loading={rulesLoading}
        fields={ruleFields}
        data={rules}
        totalPages={0}
        changePage={() => { /* rules are not paginated */ }}
      />

      {/* Modal criar/editar */}
      {modalOpen && (
        <RuleModal
          visible={modalOpen}
          layout={activeLayout}
          editingRule={editingRule}
          onClose={onCloseModal}
          onSave={onSave}
          nationalServices={nationalServices}
          complementaryLaws={complementaryLaws}
          pickerLoading={pickerLoading}
          onSearchService={onSearchService}
        />
      )}

      {/* Confirm delete */}
      <S.ConfirmDialogStyled
        visible={!!confirmDelete}
        onHide={() => setConfirmDelete(null)}
        header="Confirmar exclusão"
        message={`Excluir regra "${confirmDelete?.serviceLabel || "Todos os serviços"}"?`}
        className="confirm-custom-dialog"
        acceptLabel="Sim"
        rejectLabel="Não"
        accept={() => {
          if (confirmDelete) {
            onDelete(confirmDelete.id);
            setConfirmDelete(null);
          }
        }}
        reject={() => setConfirmDelete(null)}
      />
    </S.Wrapper>
  );
};

export default TaxationCodeAdminPage;
